export interface AvailableSalon {
    id: string;
    name: string;
}

export interface AuthResponse {
    token: string;
    id: string;
    email: string;
    fullName: string;
    pictureUrl: string;
    role: string;
    salonId: string | null;
    onboardingCompleted: boolean;
    tutorialCompleted: boolean;
    emailVerified: boolean;
    subscriptionPlan: string;
    subscriptionExpiresAt: string | null;
    availableSalons: AvailableSalon[];
    twoFactorRequired?: boolean;
    twoFactorEnabled?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

export const authApi = {
    googleLogin: async (googleToken: string): Promise<AuthResponse> => {
        const response = await fetch(`${API_URL}/auth/google`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ googleToken }),
        });

        if (!response.ok) {
            throw new Error('Google login failed');
        }

        return response.json();
    },

    emailLogin: async (email: string, password: string): Promise<AuthResponse> => {
        const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Credenciales inválidas');
            }
            throw new Error('Error en el login');
        }

        return response.json();
    },

    register: async (data: { fullName: string; email: string; password: string; consentAccepted: boolean }): Promise<{ message: string }> => {
        const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (response.status === 409) {
                throw new Error('Este email ya está registrado');
            }
            throw new Error(errorData.message || 'Error al registrar');
        }

        return response.json();
    },

    verifyEmail: async (token: string): Promise<{ message: string }> => {
        const response = await fetch(`${API_URL}/auth/verify?token=${token}`, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error('Token de verificación inválido o expirado');
        }

        return response.json();
    },

    resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
        const response = await fetch(`${API_URL}/auth/resend-verification`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Error al reenviar el correo');
        }

        return response.json();
    },

    getMe: async (): Promise<AuthResponse> => {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/users/me`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error('Error al obtener usuario');
        }

        return response.json();
    },

    verifyTwoFactor: async (email: string, code: string): Promise<AuthResponse> => {
        const response = await fetch(`${API_URL}/auth/2fa/verify`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, code }),
        });

        if (!response.ok) {
            if (response.status === 403 || response.status === 401) {
                throw new Error('Código 2FA inválido');
            }
            throw new Error('Error al verificar código');
        }

        return response.json();
    }
};

